import { logout as performLogout } from 'src/services/logout';

export async function login(context, { username, password, invite }) {
  const result = await context.state.broker.fetchAndTransform({
    fn: 'logIn',
    params: {
      body: {
        username,
        password,
      },
    },
  });

  if (result.status === 401 || result.error) {
    throw new Error(result.data?.message || result.error || 'Access denied');
  }

  await context.dispatch('session/setProfile', result.data, { root: true });

  let routeName = 'default';

  // Handle invitations
  if (invite) {
    await context.dispatch('users/acceptInvite', invite.token, { root: true });
    routeName = 'inviteWelcome';
  }

  try {
    await this.$router.push({ name: routeName });
  } catch (error) {
    // Route change was aborted (always aborts after redirect)
    if (error) {
      throw error;
    }
  }
}

/**
 * Logs user out of the app via the API and reloads the page.
 *
 * @param {ActionContext} context
 */
export async function logout() {
  await performLogout();
}

/**
 * Sets the app's state to "ready".
 *
 * @param {ActionContext} context
 */
export function ready(context) {
  context.commit('ready');
}

/**
 * Sets the app's router ready state to "ready".
 *
 * @param {ActionContext} context
 */
export function routerReady(context) {
  context.commit('routerReady');
}

/**
 * Sets the app account settings dialog state to the given value.
 *
 * @param {ActionContext} context
 */
export function setAccountSettingsDialogState(context, value) {
  context.commit('setAccountSettingsDialogState', value);
}

/**
 * Sets the app device info.
 *
 * @param {ActionContext} context
 * @param {Object} deviceInfo
 */
export function setDeviceInfo(context, deviceInfo) {
  context.commit('setDeviceInfo', deviceInfo);
}

export function setDrawerOpen() {
  // action handled in a boot file (Capacitor specific)
}

/**
 * Sets the app Live Update info.
 *
 * @param {ActionContext} context
 * @param {Object} liveUpdateInfo
 */
export function setLiveUpdateInfo(context, liveUpdateInfo) {
  context.commit('setLiveUpdateInfo', liveUpdateInfo);
}

/**
 * Sets the main nav open state.
 *
 * @param {ActionContext} context
 * @param {Boolean} isOpen
 */
export function setMainNavOpen(context, isOpen) {
  context.dispatch('setDrawerOpen', isOpen);
  context.commit('setMainNavOpen', isOpen);
}

/**
 * Sets the size of the iOS notch.
 *
 * @param {ActionContext} context
 * @param {Number} size
 */
export function setNotchSizes(context, sizes) {
  context.commit('setNotchSizes', sizes);
}

/**
 * Sets the orientation of the device.
 *
 * @param {ActionContext} context
 * @param {Number} orientation
 */
export function setOrientation(context, orientation) {
  context.commit('setOrientation', orientation);
}

/**
 * Toggles the main nav open state.
 *
 * @param {ActionContext} context
 */
export function toggleMainNav(context) {
  context.dispatch('setMainNavOpen', !context.state.mainNavOpen);
}

/**
 * Sets flags based on the type of route.
 * - e.g. filterable route, Live Map route
 *
 * @param {ActionContext} context
 */
export function watchNavigation(context) {
  this.$router.afterEach(({ matched }) => {
    const routeMatches = matched || [];
    const isFilterRoute = routeMatches.some(
      ({ meta: { hasUniversalSearchFiltering } = {} }) => hasUniversalSearchFiltering
    );
    const isLiveMapRoute = routeMatches.some(({ path }) => path === '');
    context.commit('setFilterableRoute', isFilterRoute);
    context.commit('setLiveMapRoute', isLiveMapRoute);
  });
}

/**
 * Sets the app's workers ready state to "ready".
 *
 * @param {ActionContext} context
 */
export function workersReady(context) {
  context.commit('workersReady');
}
